import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { useActionPermission } from "@/modules/auth/presentation/model/useActionPermission";
import { Breadcrumb } from "@/shared/ui/Breadcrumb";
import { SimpleSelect } from "@/shared/ui/SimpleSelect";
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Input,
  Label,
  Switch,
  toast,
} from "@devhop/ui";
import { useNavigate } from "@tanstack/react-router";
import { ArrowRightIcon, SparklesIcon } from "lucide-react";
import {
  JAM_EVENT_STATUS_HINTS,
  JAM_EVENT_STATUS_LABELS,
  JAM_EVENT_STATUS_VARIANTS,
  JAM_FORMAT_LABELS,
  JAM_KIND_LABELS,
} from "../lib/jam-status";
import {
  useInitializeFanglaoJam2026Mutation,
  useJamEventQuery,
  useJamEventsQuery,
  useUpdateJamCategoryMutation,
  useUpdateJamEventMutation,
} from "../api/queries";

function toDatetimeLocalInput(value: string | null | undefined): string {
  if (!value?.trim()) return "";
  const normalized = value.includes("T")
    ? value.slice(0, 16)
    : value.replace(" ", "T").slice(0, 16);
  return normalized;
}

function fromDatetimeLocalInput(value: string): string | null {
  const trimmed = value.trim();
  if (!trimmed) return null;
  return `${trimmed.replace("T", " ")}:00.000`;
}

export function JamEventConfigPage() {
  const nav = useNavigate();
  const canUpdate = useActionPermission(["jam-events:update"]);
  const { data: eventsData, isLoading: eventsLoading } = useJamEventsQuery();
  const event = eventsData?.items[0];
  const initMutation = useInitializeFanglaoJam2026Mutation();
  const { data: detail, isLoading: detailLoading } = useJamEventQuery(
    event?.id,
  );
  const updateMutation = useUpdateJamEventMutation();
  const updateCategoryMutation = useUpdateJamCategoryMutation();

  const handleInit = async () => {
    try {
      const result = await initMutation.mutateAsync();
      toast.success(
        result.eventCreated
          ? "ສ້າງ Fanglao Jam 2026 ແລ້ວ"
          : "ອັບເດດໝວດໝູ່ Jam ແລ້ວ",
      );
    } catch {
      toast.error("ບໍ່ສາມາດ initialize ໄດ້");
    }
  };

  const handleStatusChange = async (status: "draft" | "open" | "closed") => {
    if (!event?.id) return;
    try {
      await updateMutation.mutateAsync({ id: event.id, body: { status } });
      toast.success("ອັບເດດສະຖານະແລ້ວ");
    } catch {
      toast.error("ບໍ່ສາມາດອັບເດດສະຖານະ");
    }
  };

  return (
    <>
      <Header>
        <Breadcrumb
          items={[
            { label: "Dashboard", href: "/app/dashboard" },
            { label: "ຕັ້ງຄ່າ Fanglao Jam" },
          ]}
        />
      </Header>
      <Main>
        <div className="mb-6 flex flex-wrap items-center justify-between gap-4">
          <div>
            <h1 className="font-heading text-2xl font-bold">ຕັ້ງຄ່າ Fanglao Jam</h1>
            <p className="text-muted-foreground text-sm">
              ຈັດການເປີດ/ປິດຮັບສະໝັກ ແລະ capacity ແຕ່ລະໝວດ
            </p>
          </div>
          {canUpdate ? (
            <Button
              variant="outline"
              onClick={handleInit}
              disabled={initMutation.isPending}
            >
              <SparklesIcon className="mr-2 size-4" />
              Initialize Jam 2026
            </Button>
          ) : null}
        </div>

        {eventsLoading || detailLoading ? (
          <p className="text-muted-foreground text-sm">ກຳລັງໂຫຼດ...</p>
        ) : !event ? (
          <Card>
            <CardHeader>
              <CardTitle>ຍັງບໍ່ມີ event</CardTitle>
              <CardDescription>
                ກົດ Initialize Jam 2026 ເພື່ອ seed event ແລະໝວດແຂ່ງຂັນ
              </CardDescription>
            </CardHeader>
          </Card>
        ) : (
          <div className="flex flex-col gap-6">
            <Card>
              <CardHeader>
                <CardTitle>{detail?.title ?? event.title}</CardTitle>
                <CardDescription>slug: {event.slug}</CardDescription>
              </CardHeader>
              <CardContent className="flex flex-col gap-4">
                <div className="flex flex-wrap items-center gap-3">
                  <Badge variant={JAM_EVENT_STATUS_VARIANTS[event.status] ?? "outline"}>
                    {JAM_EVENT_STATUS_LABELS[event.status] ?? event.status}
                  </Badge>
                  <span className="text-muted-foreground text-sm">
                    {JAM_EVENT_STATUS_HINTS[event.status]}
                  </span>
                </div>
                {canUpdate ? (
                  <>
                    <div className="flex max-w-xs flex-col gap-2">
                      <Label>ສະຖານະຮັບສະໝັກ</Label>
                      <SimpleSelect
                        value={event.status}
                        onValueChange={(v) =>
                          handleStatusChange(v as "draft" | "open" | "closed")
                        }
                        options={[
                          { value: "draft", label: JAM_EVENT_STATUS_LABELS.draft! },
                          { value: "open", label: JAM_EVENT_STATUS_LABELS.open! },
                          { value: "closed", label: JAM_EVENT_STATUS_LABELS.closed! },
                        ]}
                      />
                    </div>
                    <div className="grid max-w-md gap-3 sm:grid-cols-2">
                      <div className="flex flex-col gap-1">
                        <Label htmlFor="jam-opens-at">ເປີດຮັບ (opensAt)</Label>
                        <Input
                          id="jam-opens-at"
                          type="datetime-local"
                          defaultValue={toDatetimeLocalInput(event.opensAt)}
                          onBlur={async (e) => {
                            const value = fromDatetimeLocalInput(e.target.value);
                            try {
                              await updateMutation.mutateAsync({
                                id: event.id,
                                body: { opensAt: value },
                              });
                              toast.success("ອັບເດດເວລາເປີດຮັບແລ້ວ");
                            } catch {
                              toast.error("ອັບເດດເວລາເປີດຮັບບໍ່ສຳເລັດ");
                            }
                          }}
                        />
                      </div>
                      <div className="flex flex-col gap-1">
                        <Label htmlFor="jam-closes-at">ປິດຮັບ (closesAt)</Label>
                        <Input
                          id="jam-closes-at"
                          type="datetime-local"
                          defaultValue={toDatetimeLocalInput(event.closesAt)}
                          onBlur={async (e) => {
                            const value = fromDatetimeLocalInput(e.target.value);
                            try {
                              await updateMutation.mutateAsync({
                                id: event.id,
                                body: { closesAt: value },
                              });
                              toast.success("ອັບເດດເວລາປິດຮັບແລ້ວ");
                            } catch {
                              toast.error("ອັບເດດເວລາປິດຮັບບໍ່ສຳເລັດ");
                            }
                          }}
                        />
                      </div>
                    </div>
                  </>
                ) : null}
                <Button
                  variant="outline"
                  className="w-fit"
                  onClick={() => nav({ to: "/app/jam-registrations" })}
                >
                  ໄປໃບສະໝັກ Jam
                  <ArrowRightIcon className="ml-2 size-4" />
                </Button>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>ໝວດແຂ່ງຂັນ</CardTitle>
                <CardDescription>
                  claimed / capacity — null capacity = ບໍ່ຈຳກັດ
                </CardDescription>
              </CardHeader>
              <CardContent className="flex flex-col gap-4">
                {(detail?.categories ?? []).map((cat) => (
                  <div
                    key={cat.id}
                    className="rounded-lg border p-4 flex flex-col gap-3"
                  >
                    <div className="flex flex-wrap items-center gap-2">
                      <p className="font-medium">{cat.name}</p>
                      <Badge variant="outline">{JAM_KIND_LABELS[cat.kind]}</Badge>
                      <Badge variant="secondary">{JAM_FORMAT_LABELS[cat.format]}</Badge>
                      <Badge variant={cat.availability === "full" ? "destructive" : "default"}>
                        {cat.claimed ?? 0}
                        {cat.capacity != null ? ` / ${cat.capacity}` : " / ∞"}
                      </Badge>
                    </div>
                    {canUpdate ? (
                      <div className="grid gap-3 sm:grid-cols-3">
                        <div className="flex items-center gap-2">
                          <Switch
                            checked={cat.enabled}
                            onCheckedChange={async (enabled) => {
                              try {
                                await updateCategoryMutation.mutateAsync({
                                  eventId: event.id,
                                  categoryId: cat.id,
                                  body: { enabled },
                                });
                              } catch {
                                toast.error("ອັບເດດບໍ່ສຳເລັດ");
                              }
                            }}
                          />
                          <Label>ເປີດຮັບ</Label>
                        </div>
                        <div className="flex flex-col gap-1">
                          <Label>Capacity</Label>
                          <Input
                            type="number"
                            min={cat.claimed ?? 0}
                            defaultValue={cat.capacity ?? ""}
                            placeholder="ບໍ່ຈຳກັດ"
                            onBlur={async (e) => {
                              const raw = e.target.value.trim();
                              const capacity = raw === "" ? null : Number(raw);
                              if (raw !== "" && Number.isNaN(capacity)) return;
                              try {
                                await updateCategoryMutation.mutateAsync({
                                  eventId: event.id,
                                  categoryId: cat.id,
                                  body: { capacity },
                                });
                                toast.success("ອັບເດດ capacity ແລ້ວ");
                              } catch {
                                toast.error("ອັບເດດ capacity ບໍ່ສຳເລັດ");
                              }
                            }}
                          />
                        </div>
                      </div>
                    ) : null}
                  </div>
                ))}
              </CardContent>
            </Card>
          </div>
        )}
      </Main>
    </>
  );
}
